import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Mail, Lock, User, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { LegalFooter } from '../components/LegalFooter';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('teacher');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, role);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const roles = [
    { value: 'teacher', label: 'Teacher' },
    { value: 'therapist', label: 'Therapist' },
    { value: 'case_manager', label: 'Case Manager' },
    { value: 'parent', label: 'Parent/Guardian' },
    { value: 'viewer', label: 'Viewer' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#F3F4F1] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/039b4e73-a602-4202-ac7a-82ca5d9fd25c/images/931e2859eedeb93ca67e6a32e2d6a2eba198a4aeb6067c8fec0056e4bc771f40.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative text-center max-w-md">
          <div className="w-20 h-20 rounded-3xl bg-[#2C4C3B]/10 flex items-center justify-center mx-auto mb-8">
            <GraduationCap className="h-10 w-10 text-[#2C4C3B]" />
          </div>
          <h2 className="font-outfit text-3xl font-semibold text-[#1C1C1C] mb-3">
            Join the FrameEd Ecosystem
          </h2>
          <p className="text-[#595959] text-lg leading-relaxed">
            Connect with schools, therapists, and families in one unified platform.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12" data-testid="logo-link">
            <GraduationCap className="h-8 w-8 text-[#2C4C3B]" />
            <span className="font-outfit text-2xl font-semibold text-[#1C1C1C]">FrameEd</span>
          </Link>

          <h1 className="font-outfit text-3xl font-semibold text-[#1C1C1C] mb-2">
            Create your account
          </h1>
          <p className="text-[#595959] mb-8">
            Start building better outcomes with FrameEd
          </p>

          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700" data-testid="register-error">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#1C1C1C]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="pl-10 rounded-xl border-[#E5E5E5] focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1C1C1C]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu"
                  className="pl-10 rounded-xl border-[#E5E5E5] focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1C1C1C]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  className="pl-10 pr-10 rounded-xl border-[#E5E5E5] focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#595959] hover:text-[#1C1C1C]"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-[#1C1C1C]">I am a...</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="rounded-xl border-[#E5E5E5]" data-testid="role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl py-6"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E5E5E5]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#595959]">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full mt-4 border-[#E5E5E5] text-[#1C1C1C] hover:bg-[#F3F4F1] rounded-xl py-6"
              data-testid="google-register-btn"
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </div>

          <p className="mt-8 text-center text-[#595959]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2C4C3B] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      </div>
      <LegalFooter variant="compact" />
    </div>
  );
}
